import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { lazy, Suspense } from 'react'

import { AppShell } from '../components/AppShell.js'
import { Unavailable } from '../components/Unavailable.js'
import { isPublicPath, isSignedOut, meQuery } from '../lib/session.js'

export interface RouterContext {
  queryClient: QueryClient
}

// Neither toasts nor the 404 page are needed for the first paint; both load in their own chunk
// to keep the initial JavaScript within the spec 004 budget.
const Toaster = lazy(() => import('sonner').then(module => ({ default: module.Toaster })))
const NotFound = lazy(() => import('../components/NotFound.js'))
// Only while an admin views the app as a user (spec 012).
const ImpersonationBanner = lazy(() => import('../components/ImpersonationBanner.js'))

export const Route = createRootRouteWithContext<RouterContext>()({
  // Every page except sign-in needs a session (spec 008). Not signed in → /sign-in?next=…; the API
  // not answering is an outage and shows as one, it never signs the user out.
  beforeLoad: async ({ context, location }) => {
    if (isPublicPath(location.pathname)) {
      return
    }
    try {
      await context.queryClient.query(meQuery)
    } catch (error) {
      if (isSignedOut(error)) {
        throw redirect({ to: '/sign-in', search: { next: location.href } })
      }
      throw error
    }
  },
  component: RootLayout,
  errorComponent: RootError,
  notFoundComponent: () => (
    <Suspense fallback={null}>
      <NotFound />
    </Suspense>
  ),
})

function RootLayout() {
  const pathname = useRouterState({ select: state => state.location.pathname })
  if (pathname === '/sign-in') {
    return <Outlet />
  }
  return (
    <AppShell>
      {!isPublicPath(pathname) && (
        <Suspense fallback={null}>
          <ImpersonationBanner />
        </Suspense>
      )}
      <Outlet />
      <Suspense fallback={null}>
        <Toaster position="top-center" />
      </Suspense>
    </AppShell>
  )
}

function RootError() {
  const router = useRouter()
  return (
    <AppShell>
      <Unavailable onRetry={() => void router.invalidate()} />
    </AppShell>
  )
}
